import { type FunctionComponent, type JSX, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import mobileGraphic from '../../assets/images/mobile-graphic.png?url';
import { Button } from '../../atoms/Button/Button';
import { registerPrayerStreamUserSlice } from '../../core/async-action/register-prayer-stream-user/register-prayer-stream-user.slice';
import {
  PrayerStreamUserStatus,
  streamSlice,
} from '../../core/stream/stream.slice';
import { useSelector } from '../../hooks/useSelector';
import { useTranslation } from '../../i18n/resources';
import { GroupManagementAlertDialog } from '../GroupManagementAlertDialog/GroupManagementAlertDialog';
import { Layout } from '../Layout/Layout';
import { OnboardingCompleteAlertDialog } from '../OnboardingCompleteAlertDialog/OnboardingCompleteAlertDialog';
import { PageSection } from '../PageSection/PageSection';
import { PrayerStreamLogo } from '../PrayerStreamLogo/PrayerStreamLogo';
import { UserAgreementAlertDialog } from '../UserAgreementAlertDialog/UserAgreementAlertDialog';
import styles from './Landing.module.css';

export const Landing: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { communityChannelData, prayerStreamUserStatus } = useSelector(
    (state) => state.stream,
  );
  const [agreementOpen, setAgreementOpen] = useState<boolean>(false);
  const [userSelectionSkipped, setUserSelectionSkipped] =
    useState<boolean>(false);
  const [onboardingCompletedOpen, setOnboardingCompletedOpen] =
    useState<boolean>(false);

  const handleCompletedUserAgreement = useCallback((): void => {
    dispatch(
      registerPrayerStreamUserSlice.actions.registerPrayerStreamUserRequested(),
    );
  }, [dispatch]);

  const renderOnboardingComponents = (): JSX.Element | undefined => {
    if (prayerStreamUserStatus === PrayerStreamUserStatus.NOT_REGISTERED) {
      return (
        <UserAgreementAlertDialog
          onClose={() => {
            setAgreementOpen(false);
          }}
          onSubmit={() => {
            handleCompletedUserAgreement();
            setAgreementOpen(false);
          }}
          open={agreementOpen}
        />
      );
    }

    if (
      prayerStreamUserStatus ===
        PrayerStreamUserStatus.SELECT_COMMUNITY_GROUPS &&
      communityChannelData
    ) {
      return (
        <GroupManagementAlertDialog
          memberChannelData={communityChannelData}
          onboarding
          onClose={({ userClosed }) => {
            dispatch(streamSlice.actions.setCommunityGroupList(null));
            setUserSelectionSkipped(userClosed);
            setOnboardingCompletedOpen(true);
          }}
        />
      );
    }

    return;
  };

  return (
    <div className={styles.root}>
      <Layout>
        <PageSection>
          <div className={styles.container}>
            <div className={styles.info}>
              <PrayerStreamLogo />
              <div className={styles.header}>{t('landingPage', 'header')}</div>
              <div className={styles.subheader}>
                {t('landingPage', 'subheader')}
              </div>
              <Button
                onClick={() => {
                  setAgreementOpen(true);
                }}
              >
                {t('landingPage', 'get_started')}
              </Button>
              {renderOnboardingComponents()}
              {onboardingCompletedOpen && (
                <OnboardingCompleteAlertDialog
                  onClose={() => {
                    dispatch(
                      streamSlice.actions.setPrayerStreamUserStatus(
                        PrayerStreamUserStatus.REGISTERED,
                      ),
                    );
                    setOnboardingCompletedOpen(false);
                  }}
                  userSelectionSkipped={userSelectionSkipped}
                />
              )}
            </div>
            <div className={styles.image}>
              <img
                alt="PrayerStream Mobile Graphic"
                height={462}
                src={mobileGraphic}
              />
            </div>
          </div>
        </PageSection>
      </Layout>
    </div>
  );
};
